import { z } from "zod";

// The athlete's Health Connect consent record (#42, Antenore 02/10). A small
// history, not a flag: every grant and every withdrawal is one event, so the
// record can say "given on X for these categories, withdrawn on Y". Lives on
// the Profile row (server-managed, never written by PUT) and is cached on the
// phone in hcConsent.ts with the same shape.
//
// ⛔ Not the CO-9 per-metric coach consent: that one is athlete-to-coach
// sharing on the coach edge. This one is athlete-to-Metria, for the reads.
//
// A category is one OS permission as Android granted it, `read:Weight` or
// `write:Weight`. The access type stays in the id so the write consent is
// provable on its own.

const HealthConsentCategory = z
  .string()
  .regex(/^(read|write):[A-Za-z]{1,60}$/);

const Categories = z.array(HealthConsentCategory).min(1).max(32);

const GrantBody = z.object({
  kind: z.literal("granted"),
  /** Device time of the grant. */
  at: z.iso.datetime(),
  categories: Categories,
});

const RevokeBody = z.object({
  kind: z.literal("revoked"),
  /** Device time of the withdrawal. */
  at: z.iso.datetime(),
});

/** Client to server: the event as the phone saw it. */
export const NewHealthConsentEvent = z.discriminatedUnion("kind", [
  GrantBody,
  RevokeBody,
]);
export type NewHealthConsentEvent = z.infer<typeof NewHealthConsentEvent>;

// `recordedAt` is the server's clock, stamped on receipt. Absent only in the
// phone's cache, for an event still on its way.
const RecordedAt = { recordedAt: z.iso.datetime().optional() };

export const HealthConsentEvent = z.discriminatedUnion("kind", [
  GrantBody.extend(RecordedAt),
  RevokeBody.extend(RecordedAt),
]);
export type HealthConsentEvent = z.infer<typeof HealthConsentEvent>;

/** Two hundred events is a hundred connect and disconnect cycles. */
export const HEALTH_CONSENT_HISTORY_MAX = 200;

export const HealthConsentHistory = z
  .array(HealthConsentEvent)
  .max(HEALTH_CONSENT_HISTORY_MAX);
export type HealthConsentHistory = z.infer<typeof HealthConsentHistory>;

const eventKey = (e: HealthConsentEvent) => `${e.kind}|${e.at}`;

/** When the athlete gave the event (Antenore 03/10): its device time, but
 *  never later than the server received it, so a phone clock running ahead
 *  cannot put an event after every real one. */
export const healthConsentEventTime = (e: {
  at: string;
  recordedAt?: string;
}): number => {
  const at = Date.parse(e.at);
  return e.recordedAt === undefined ? at : Math.min(at, Date.parse(e.recordedAt));
};

/** Oldest first, by when the athlete gave the event: a withdrawal that
 *  reaches the server late takes its own place and changes nothing after
 *  it. Ties: the one recorded first, an unrecorded one after, a grant
 *  before a withdrawal. */
export const compareHealthConsentEvents = (
  a: HealthConsentEvent,
  b: HealthConsentEvent,
): number => {
  const t = healthConsentEventTime(a) - healthConsentEventTime(b);
  if (t !== 0) return t;
  if (a.recordedAt !== b.recordedAt) {
    if (a.recordedAt === undefined) return 1;
    if (b.recordedAt === undefined) return -1;
    const r = Date.parse(a.recordedAt) - Date.parse(b.recordedAt);
    if (r !== 0) return r;
  }
  if (a.kind === b.kind) return 0;
  return a.kind === "granted" ? -1 : 1;
};

/** The event that decides the standing, or undefined for an empty record. */
export const latestHealthConsentEvent = (
  history: readonly HealthConsentEvent[],
): HealthConsentEvent | undefined => {
  const sorted = [...history].sort(compareHealthConsentEvents);
  return sorted[sorted.length - 1];
};

/**
 * The server's history plus the local events it has not seen yet, oldest
 * first (see compareHealthConsentEvents). An event is the same event when kind and device time
 * match; the server copy wins because it carries `recordedAt`.
 */
export function mergeHealthConsentHistory(
  local: readonly HealthConsentEvent[],
  server: readonly HealthConsentEvent[],
): HealthConsentEvent[] {
  const byKey = new Map<string, HealthConsentEvent>();
  for (const e of local) byKey.set(eventKey(e), e);
  for (const e of server) byKey.set(eventKey(e), e);
  return [...byKey.values()]
    .sort(compareHealthConsentEvents)
    .slice(-HEALTH_CONSENT_HISTORY_MAX);
}

export interface HealthConsentStanding {
  /** The latest grant, if any. */
  granted?: { at: string; categories: string[] };
  /** The withdrawal that followed it (or the last one, with no grant). */
  revokedAt?: string;
}

/** What the record says today: the last grant, and whether it was withdrawn. */
export function healthConsentStanding(
  history: readonly HealthConsentEvent[],
): HealthConsentStanding {
  let granted: HealthConsentStanding["granted"];
  let revokedAt: string | undefined;
  for (const e of [...history].sort(compareHealthConsentEvents)) {
    if (e.kind === "granted") {
      granted = { at: e.at, categories: e.categories };
      revokedAt = undefined;
    } else {
      revokedAt = e.at;
    }
  }
  return { granted, revokedAt };
}

/** How a connected phone and the account's record disagree, or null:
 *  `deviceOff` the record holds a consent Android no longer allows here,
 *  `undated` the opt-in is older than the record, `noRecord` Android allows
 *  what the record does not hold. `deviceAllows` null: not checked yet. */
export type HealthConsentDrift = "deviceOff" | "undated" | "noRecord";

export function healthConsentDrift(
  history: readonly HealthConsentEvent[],
  optedIn: boolean,
  deviceAllows: boolean | null,
): HealthConsentDrift | null {
  if (!optedIn || deviceAllows === null) return null;
  const { granted, revokedAt } = healthConsentStanding(history);
  const current = granted !== undefined && revokedAt === undefined;
  if (current === deviceAllows) return null;
  if (current) return "deviceOff";
  return granted === undefined ? "undated" : "noRecord";
}

// The consent category each kind of Health Connect import needs (#42, the
// permissions of HC-18): a write is allowed only when the recorded grant
// lists them all. One table for the server's check and the phone's filter.
export const HC_IMPORT_CATEGORY = {
  /** A workout import or enrich (sessions, their sets). */
  exercise: "read:ExerciseSession",
  glucose: "read:BloodGlucose",
  nutrition: "read:Nutrition",
} as const;

const MEASUREMENT_FIELD_CATEGORY: Record<string, string> = {
  weightKg: "read:Weight",
  bodyFatPct: "read:BodyFat",
  leanMassKg: "read:LeanBodyMass",
  // No longer read since HC-18, still accepted by the schema.
  boneMassKg: "read:BoneMass",
  bodyWaterKg: "read:BodyWaterMass",
  bmrKcalDay: "read:BasalMetabolicRate",
};

/** The categories a measurement import needs: one per value it carries
 *  (weight with none of them: the scale's own category). */
export function measurementImportCategories(body: unknown): string[] {
  const o = (body ?? {}) as Record<string, unknown>;
  const cats = Object.keys(MEASUREMENT_FIELD_CATEGORY)
    .filter((k) => o[k] !== undefined && o[k] !== null)
    .map((k) => MEASUREMENT_FIELD_CATEGORY[k]!);
  return cats.length > 0 ? [...new Set(cats)] : ["read:Weight"];
}

/** The categories the account's record grants for imports now: those of
 *  its latest grant when the record ends on one, else none. */
export function openHealthImportCategories(
  history: readonly HealthConsentEvent[],
): string[] {
  const latest = latestHealthConsentEvent(history);
  return latest?.kind === "granted" ? [...latest.categories] : [];
}
